import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Ticket, CheckCircle, AlertCircle, Loader, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import ThankYouEvent from './v2/ThankYouEvent';

// Helper functions
const getCurrencySymbol = (currency) => {
    const symbols = {
        'INR': '₹',
        'USD': '$',
        'EUR': '€',
        'GBP': '£'
    };
    return symbols[currency] || currency;
};

const calculateTicketPrice = (ticket) => {
    if (!ticket.priceByDate || ticket.priceByDate.length === 0) {
        return ticket.price || 0;
    }
    const regDate = new Date();
    // Sort by end date
    const sortedPrices = [...ticket.priceByDate].sort((a, b) =>
        new Date(a.endDate) - new Date(b.endDate)
    );

    for (const tier of sortedPrices) {
        const startDate = tier.startDate ? new Date(tier.startDate) : new Date(0);
        const endDate = new Date(tier.endDate);
        if (regDate >= startDate && regDate <= endDate) {
            return tier.price;
        }
    }
    // Fallback to last tier or base price
    return sortedPrices[sortedPrices.length - 1]?.price || ticket.price || 0;
};

const PublicRegistration = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({});
    const [completedParticipant, setCompletedParticipant] = useState(null);

    // Ticket States
    const [selectedNormalTicket, setSelectedNormalTicket] = useState('');
    const [selectedCategoryTickets, setSelectedCategoryTickets] = useState({}); // { categoryName: ticketId }

    // Region State
    const [selectedRegion, setSelectedRegion] = useState(null);

    // Participant category (for free events)
    const [selectedParticipantCategory, setSelectedParticipantCategory] = useState('');
    const [catSearch, setCatSearch] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const participantDetailsRef = useRef(null);
    const [registrationId, setRegistrationId] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [expandedCategories, setExpandedCategories] = useState({});

    useEffect(() => {
        fetchEventDetails();
    }, [eventId]);

    const fetchEventDetails = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/public/${eventId}`
            );
            const data = await response.json();

            if (data.success) {
                setEvent(data.data);
                // Initialize form data
                const initialFormData = {};
                data.data.participantFields?.forEach(field => {
                    initialFormData[field.id] = field.fieldType === 'checkbox' ? [] : '';
                });
                setFormData(initialFormData);

                // Set default region
                if (data.data.ticketRegions && data.data.ticketRegions.length > 0) {
                    setSelectedRegion(data.data.ticketRegions[0]);
                }

                // Initialize expanded categories
                const categories = {};
                const uniqueCategories = [...new Set(data.data.tickets
                    ?.filter(t => t.ticketType === 'category')
                    .map(t => t.categoryName))];
                uniqueCategories.forEach(cat => categories[cat] = true);
                setExpandedCategories(categories);

            } else {
                setError(data.message || 'Event not found');
            }
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details. Please check the link and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegionChange = (region) => {
        setSelectedRegion(region);
        setSelectedNormalTicket('');
        setSelectedCategoryTickets({});
    };

    const handleInputChange = (fieldId, value, type = 'text') => {
        setFormData(prev => {
            if (type === 'checkbox') {
                const currentValues = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
                if (currentValues.includes(value)) {
                    return { ...prev, [fieldId]: currentValues.filter(v => v !== value) };
                } else {
                    return { ...prev, [fieldId]: [...currentValues, value] };
                }
            }
            return {
                ...prev,
                [fieldId]: value
            };
        });
        if (validationErrors[fieldId]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldId];
                return newErrors;
            });
        }
    };

    // Filter tickets based on selected region
    const filteredTickets = event?.tickets?.filter(t => {
        if (!selectedRegion) return true;
        return t.regionType === selectedRegion.id;
    }) || [];

    const normalTickets = filteredTickets.filter(t => t.ticketType !== 'category' || !t.ticketType);
    const categoryTickets = filteredTickets.filter(t => t.ticketType === 'category');

    // Group category tickets by categoryName
    const categoryGroups = categoryTickets.reduce((groups, ticket) => {
        const name = ticket.categoryName || 'Other';
        if (!groups[name]) groups[name] = [];
        groups[name].push(ticket);
        return groups;
    }, {});

    const handleCategoryTicketSelect = (categoryName, ticketId) => {
        setSelectedCategoryTickets(prev => {
            const newSelection = { ...prev };
            // Toggle logic: if already selected, deselect it
            if (newSelection[categoryName] === ticketId) {
                delete newSelection[categoryName];
            } else {
                newSelection[categoryName] = ticketId;
            }
            return newSelection;
        });

        if (validationErrors.ticket) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.ticket;
                return newErrors;
            });
        }
    };

    const totalPrice = () => {
        let total = 0;
        const normalTicket = normalTickets.find(t => t.id === selectedNormalTicket);
        if (normalTicket) {
            total += calculateTicketPrice(normalTicket);
        }
        Object.values(selectedCategoryTickets).forEach(ticketId => {
            const ticket = categoryTickets.find(t => t.id === ticketId);
            if (ticket) {
                total += calculateTicketPrice(ticket);
            }
        });
        return total;
    };

    const validateForm = () => {
        const errors = {};
        if (event.eventType === 'paid') {
            if (!selectedNormalTicket) {
                errors.ticket = 'Please select a main ticket';
            }
        }
        const visibleCats = (event.participantCategories || []).filter(c => c.showInForm !== false);
        if (visibleCats.length > 0 && !selectedParticipantCategory) {
            errors.participantCategory = 'Please select a category';
        }
        event.participantFields?.forEach(field => {
            if (field.validation === 'required' && !formData[field.id]) {
                errors[field.id] = `This field is required`;
            }
            if (field.fieldType === 'email' && formData[field.id]) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData[field.id])) {
                    errors[field.id] = 'Please enter a valid email';
                }
            }
            // Phone validation could be added here
            if (field.fieldType === 'phone' && formData[field.id]) {
                // simple length check for now
                if (formData[field.id].length < 10) {
                    errors[field.id] = 'Please enter a valid phone number';
                }
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Load Razorpay SDK
    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async (payload) => {
        const res = await loadRazorpay();
        if (!res) {
            setError('Razorpay SDK failed to load. Are you online?');
            setSubmitting(false);
            return;
        }

        try {
            // 1. Create Order
            const orderResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/payment/order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventId,
                    data: formData,
                    ticketIds: payload.ticketIds,
                    ticketId: payload.ticketId
                })
            });

            const orderResult = await orderResponse.json();
            if (!orderResult.success) {
                setError(orderResult.message || 'Failed to create order');
                setSubmitting(false);
                return;
            }

            const { orderId, amount, currency, keyId, participantId } = orderResult.data;

            // 2. Open Razorpay Checkout
            const options = {
                key: keyId,
                amount: amount.toString(),
                currency: currency,
                name: event.eventName,
                description: "Event Registration",
                // image: event.defaultIdCardBackground, // Can add logo here
                order_id: orderId,
                handler: async function (response) {
                    try {
                        const verifyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/payment/verify`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                eventId: eventId
                            })
                        });

                        const verifyResult = await verifyResponse.json();
                        if (verifyResult.success) {
                            // setCompletedParticipant({
                            //     ...formData,
                            //     _id: verifyResult.data.participantId
                            // });
                            // setSuccess(true);
                            navigate(`/event/${eventId}/registration-success/${verifyResult.data.participantId}`);
                            window.scrollTo(0, 0);
                        } else {
                            setError('Payment verification failed');
                        }
                    } catch (err) {
                        console.error('Verification Error:', err);
                        setError('Payment verification failed');
                    }
                },
                prefill: {
                    name: formData.name || '',
                    email: formData.email || '',
                    contact: formData.phone || ''
                },
                notes: {
                    address: "Razorpay Corporate Office"
                },
                theme: {
                    color: "#2563EB"
                },
                modal: {
                    ondismiss: function () {
                        setSubmitting(false);
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();

        } catch (err) {
            console.error('Payment Error:', err);
            setError(`Payment initialization failed: ${err.message}`);
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            const firstError = document.querySelector('.text-red-500');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSubmitting(true);
        setError('');

        const ticketIds = [];
        if (selectedNormalTicket) ticketIds.push(selectedNormalTicket);
        Object.values(selectedCategoryTickets).forEach(id => ticketIds.push(id));

        const payload = {
            data: formData,
            ticketIds: ticketIds,
            ticketId: selectedNormalTicket,
            categoryId: selectedParticipantCategory || undefined
        };

        // Check if event is paid or free
        // If paid but total price is 0 (e.g. 100% discount or free ticket type), treat as free?
        // Current logic: calculate total price
        let total = totalPrice();

        if (event.eventType === 'paid' && total > 0) {
            await handlePayment(payload);
        } else {
            // Free registration
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/v2/participants/register/${eventId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    }
                );

                const result = await response.json();

                if (result.success) {
                    // setSuccess(true);
                    // setRegistrationId(result.data.registrationId);
                    // setCompletedParticipant({
                    //     ...formData,
                    //     _id: result.data._id
                    // });
                    navigate(`/event/${eventId}/registration-success/${result.data._id}`);
                    window.scrollTo(0, 0);
                } else {
                    setError(result.message || 'Registration failed');
                }
            } catch (err) {
                console.error('Registration error:', err);
                setError('An unexpected error occurred. Please try again.');
            } finally {
                setSubmitting(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading event details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    // if (success) {
    //     return (
    //         <ThankYouEvent
    //             event={event}
    //             registrationId={registrationId}
    //             participant={completedParticipant}
    //         />
    //     );
    // }

    const currentCurrency = selectedRegion?.currency || event.currency || 'INR';

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                    <div className="relative h-[150px] sm:h-[100px] md:h-[150px] bg-slate-900">
                        {event.defaultIdCardBackground ? (
                            <img
                                src={event.defaultIdCardBackground}
                                alt={event.eventName}
                                className="w-full h-full object-cover opacity-80"
                            />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700" />
                        )}
                        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 shadow-sm">
                                {event.eventName}
                            </h1>
                            <div className="flex flex-wrap gap-4 text-white/90 text-sm sm:text-base">
                                <span className="flex items-center backdrop-blur-sm bg-white/10 px-3 py-1 rounded-full">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    {new Date(event.eventStartDate).toLocaleDateString()}
                                    {event.eventEndDate && ` - ${new Date(event.eventEndDate).toLocaleDateString()}`}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {event.ticketRegions && event.ticketRegions.length > 0 && (
                        <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-100 flex space-x-2 overflow-x-auto">
                            {event.ticketRegions.map(region => (
                                <button
                                    key={region.id}
                                    type="button"
                                    onClick={() => handleRegionChange(region)}
                                    className={`
                                        flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all whitespace-nowrap
                                        ${selectedRegion?.id === region.id
                                            ? 'bg-blue-50 text-blue-700 border border-blue-100 shadow-sm'
                                            : 'text-gray-600 hover:bg-gray-50'
                                        }
                                    `}
                                >
                                    {region.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {event.eventType === 'paid' && (
                        <div className="space-y-6">
                            {normalTickets.length > 0 ? (
                                <div className="space-y-3">
                                    <label className="block text-lg font-semibold text-gray-900 mb-3">
                                        Select Ticket <span className="text-red-500">*</span>
                                    </label>
                                    {normalTickets.map(ticket => (
                                        <TicketOption
                                            key={ticket.id}
                                            ticket={ticket}
                                            isSelected={selectedNormalTicket === ticket.id}
                                            onSelect={() => setSelectedNormalTicket(prev => prev === ticket.id ? '' : ticket.id)}
                                            currency={currentCurrency}
                                        />
                                    ))}
                                    {validationErrors.ticket && (
                                        <p className="text-red-500 text-sm">{validationErrors.ticket}</p>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-dashed border-gray-300">
                                    No tickets available for this region.
                                </div>
                            )}

                            {Object.entries(categoryGroups).map(([categoryName, tickets]) => (
                                <div key={categoryName} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                    <div
                                        className="bg-gray-50 p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => setExpandedCategories(prev => ({
                                            ...prev,
                                            [categoryName]: !prev[categoryName]
                                        }))}
                                    >
                                        <h3 className="font-semibold text-gray-900">{categoryName}</h3>
                                        {expandedCategories[categoryName] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                                    </div>

                                    {expandedCategories[categoryName] && (
                                        <div className="p-4 space-y-3 bg-white">
                                            {tickets.map(ticket => (
                                                <TicketOption
                                                    key={ticket.id}
                                                    ticket={ticket}
                                                    isSelected={selectedCategoryTickets[categoryName] === ticket.id}
                                                    onSelect={() => handleCategoryTicketSelect(categoryName, ticket.id)}
                                                    currency={currentCurrency}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-600">Total Price</span>
                                    <span className="text-2xl font-bold text-blue-700">
                                        {getCurrencySymbol(currentCurrency)}{totalPrice().toFixed(2)}
                                    </span>
                                </div>
                                <p className="text-xs text-blue-600 text-right">
                                    *Price calculated based on current date
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Participant Category Selection */}
                    {event.participantCategories?.length > 0 && (() => {
                        const visibleCategories = event.participantCategories.filter(c => c.showInForm !== false);
                        if (visibleCategories.length === 0) return null;

                        const filtered = visibleCategories.filter(cat =>
                            cat.name.toLowerCase().includes(catSearch.toLowerCase()) ||
                            (cat.description || '').toLowerCase().includes(catSearch.toLowerCase())
                        );
                        const selectedCat = visibleCategories.find(c => c.id === selectedParticipantCategory);

                        return (
                            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-gray-900">Select Category</h2>
                                    <span className="text-xs text-gray-400 font-medium">{visibleCategories.length} categories</span>
                                </div>

                                {/* Selected badge */}
                                {selectedCat && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                                        <CheckCircle className="w-4 h-4 text-blue-500 shrink-0" />
                                        <span className="text-sm font-semibold text-blue-700 truncate">{selectedCat.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedParticipantCategory('')}
                                            className="ml-auto text-blue-400 hover:text-blue-600"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}

                                {/* Search */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={catSearch}
                                        onChange={e => setCatSearch(e.target.value)}
                                        placeholder="Search categories..."
                                        className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                    />
                                    {catSearch && (
                                        <button type="button" onClick={() => setCatSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {/* Scrollable list */}
                                <div className="overflow-y-auto max-h-72 space-y-2 pr-1">
                                    {filtered.length === 0 ? (
                                        <p className="text-center text-sm text-gray-400 py-6">No categories match "{catSearch}"</p>
                                    ) : filtered.map(cat => (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedParticipantCategory(cat.id);
                                                setTimeout(() => {
                                                    participantDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }, 50);
                                            }}
                                            className={`w-full text-left p-3 rounded-xl border-2 transition-all ${selectedParticipantCategory === cat.id
                                                ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                : 'border-gray-100 hover:border-blue-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {cat.idCardBackground && (
                                                    <img
                                                        src={cat.idCardBackground}
                                                        alt={cat.name}
                                                        className="w-8 h-11 object-cover rounded shrink-0 border border-gray-200"
                                                    />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${selectedParticipantCategory === cat.id ? 'border-blue-500' : 'border-gray-300'
                                                            }`}>
                                                            {selectedParticipantCategory === cat.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                                                        </div>
                                                        <p className="font-semibold text-gray-900 text-sm">{cat.name}</p>
                                                    </div>
                                                    {cat.description && (
                                                        <p className="text-xs text-gray-500 mt-0.5 ml-5 line-clamp-1">{cat.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                {catSearch && filtered.length > 0 && (
                                    <p className="text-xs text-gray-400 text-center">{filtered.length} of {event.participantCategories.length} categories</p>
                                )}

                                {validationErrors.participantCategory && (
                                    <p className="text-red-500 text-sm flex items-center gap-1">
                                        <AlertCircle className="w-3.5 h-3.5" />{validationErrors.participantCategory}
                                    </p>
                                )}
                            </div>
                        );
                    })()}

                    {event.participantFields && event.participantFields.length > 0 && (
                        <div ref={participantDetailsRef} className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Participant Details</h2>
                            <div className="grid grid-cols-1 gap-6">
                                {event.participantFields.sort((a, b) => a.order - b.order).map(field => (
                                    <div key={field.id} className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {field.fieldName} {field.validation === 'required' && <span className="text-red-500">*</span>}
                                        </label>

                                        {field.fieldType === 'textarea' ? (
                                            <textarea
                                                value={formData[field.id] || ''}
                                                onChange={(e) => handleInputChange(field.id, e.target.value)}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${validationErrors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                                rows={4}
                                                placeholder={field.placeholder}
                                            />
                                        ) : field.fieldType === 'select' ? (
                                            <select
                                                value={formData[field.id] || ''}
                                                onChange={(e) => handleInputChange(field.id, e.target.value)}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${validationErrors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            >
                                                <option value="">Select an option</option>
                                                {field.options && field.options.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        ) : field.fieldType === 'checkbox' ? (
                                            <div className="space-y-2 mt-2">
                                                {field.options && field.options.map((opt, idx) => (
                                                    <label key={idx} className="flex items-center space-x-3 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={(formData[field.id] || []).includes(opt)}
                                                            onChange={() => handleInputChange(field.id, opt, 'checkbox')}
                                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-700">{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ) : field.fieldType === 'radio' ? (
                                            <div className="space-y-2 mt-2">
                                                {field.options && field.options.map((opt, idx) => (
                                                    <label key={idx} className="flex items-center space-x-3 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name={field.id}
                                                            checked={formData[field.id] === opt}
                                                            onChange={(e) => handleInputChange(field.id, opt)}
                                                            className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-700">{opt}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ) : (
                                            <input
                                                type={field.fieldType}
                                                value={formData[field.id] || ''}
                                                onChange={(e) => handleInputChange(field.id, e.target.value)}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${validationErrors[field.id] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                                placeholder={field.placeholder}
                                            />
                                        )}
                                        {field.helpText && <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>}
                                        {validationErrors[field.id] && (
                                            <p className="text-red-500 text-sm mt-1">{validationErrors[field.id]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        {submitting ? (
                            <>
                                <Loader className="w-5 h-5 mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            `
                             Submit
                             `
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

const TicketOption = ({ ticket, isSelected, onSelect, currency }) => {
    const currentPrice = calculateTicketPrice(ticket);
    const basePrice = ticket.price || 0;
    const hasDiscount = currentPrice < basePrice;

    // Helper to find active tier and next tier
    const getPricingInfo = () => {
        if (!ticket.priceByDate) return { activeTier: null, nextTier: null };
        const regDate = new Date();
        // Sort by start date to find sequence
        const sortedPrices = [...ticket.priceByDate].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        const activeIndex = sortedPrices.findIndex(tier =>
            regDate >= new Date(tier.startDate) && regDate <= new Date(tier.endDate)
        );

        const activeTier = activeIndex !== -1 ? sortedPrices[activeIndex] : null;
        const nextTier = activeIndex !== -1 && activeIndex + 1 < sortedPrices.length ? sortedPrices[activeIndex + 1] : null;

        return { activeTier, nextTier };
    };

    const { activeTier, nextTier } = getPricingInfo();

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    };

    return (
        <div
            onClick={onSelect}
            className={`
                border rounded-xl p-4 cursor-pointer transition-all relative overflow-hidden
                ${isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-md transform scale-[1.01]'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                }
            `}
        >
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="flex items-center mb-1">
                        <div className={`
                            w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center
                            ${isSelected ? 'border-blue-600' : 'border-gray-300'}
                        `}>
                            {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                        </div>
                        <h3 className={`font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                            {ticket.name}
                        </h3>
                    </div>
                    {ticket.description && (
                        <p className="text-sm text-gray-500 ml-8 mb-2 whitespace-pre-line">{ticket.description}</p>
                    )}

                    {/* Offer Infos */}
                    <div className="ml-8 space-y-1 mt-1">
                        {hasDiscount && activeTier && (
                            <div className="inline-block bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded border border-green-200">
                                Offer ends on {formatDate(activeTier.endDate)}
                            </div>
                        )}
                        {/* {nextTier && (
                            <div className="flex items-center text-xs text-amber-600 font-medium">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Price increases to {getCurrencySymbol(currency)}{nextTier.price} on {formatDate(nextTier.startDate)}
                            </div>
                        )} */}
                    </div>
                </div>
                <div className="text-right flex flex-col items-end">
                    {hasDiscount ? (
                        <>
                            <span className="text-xs text-gray-400 line-through">
                                {getCurrencySymbol(currency)}{basePrice}
                            </span>
                            <span className="text-lg font-bold text-green-600">
                                {currentPrice > 0 ? `${getCurrencySymbol(currency)}${currentPrice}` : 'Free'}
                            </span>
                        </>
                    ) : (
                        <span className="text-lg font-bold text-gray-900">
                            {currentPrice > 0 ? `${getCurrencySymbol(currency)}${currentPrice}` : 'Free'}
                        </span>
                    )}

                    {ticket.price > currentPrice && (
                        <div className="text-xs text-blue-600 font-medium mt-0.5">
                            Early Bird Offer
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicRegistration;
